/**
 * The reading view.
 *
 * This is the document as it is read *from*, not a page about the document. It
 * used to be the latter: two thirds of the screen went to Document Details and
 * Sermon Details — type, created date, word count, series, audience, tags —
 * with the sermon itself boxed into a card beside them. All of that is
 * available in the editor's sidebar, where it is being decided; none of it is
 * wanted at 11:04 on a Sunday morning.
 *
 * So what is left is the words, set large, and nothing else on screen. The
 * design follows from where it is used — an iPad on a pulpit, read at arm's
 * length, glanced at between sentences:
 *
 *   Nothing is interactive.   The text cannot be selected and raises no iOS
 *                             callout; a hand steadying the tablet does
 *                             nothing at all.
 *   The controls hide.        A deliberate tap brings them back. The button
 *                             that opens the editor is never under a thumb
 *                             that meant to scroll.
 *   The screen stays awake.   For as long as this page is open.
 *   You can find your place.  Generous leading, headings as landmarks, a
 *                             contents list, and a progress line at the top.
 *
 * The contents panel is deliberately not a Radix dialog. Radix locks the page
 * with `body { pointer-events: none }` while a modal is open, and a lock that
 * fails to lift freezes the app until a reload — which happened here (see
 * hooks/use-pointer-events-guard). Mid-sermon is the worst possible place for
 * that risk, and a panel this simple does not need the machinery.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useLocation } from "wouter";
import {
  ArrowLeft,
  FileText,
  List,
  Minus,
  Pencil,
  Plus,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDocument } from "@/hooks/use-document";
import { useWakeLock } from "@/hooks/use-wake-lock";
import { renderMarkdown } from "@/lib/markdown";
import { parseSlides } from "@/lib/slides";
import { cn } from "@/lib/utils";
import { useDesktopDocument } from "@/hooks/use-desktop-document";
import { runDocumentExport } from "@/lib/document-export";
import { useTheme } from "@/contexts/ThemeContext";

/** Text-size steps, as a multiplier on the chosen document font's own size. */
const SCALE_MIN = 1;
const SCALE_MAX = 2.6;
const SCALE_STEP = 0.15;
const SCALE_DEFAULT = 1.35;
const SCALE_KEY = "readingScale";

/** How long the controls stay up after they are summoned. */
const CHROME_TIMEOUT_MS = 4000;

interface Landmark {
  id: string;
  text: string;
  level: number;
}

/**
 * Render the document, and give every heading an id so the contents list has
 * something to jump to.
 *
 * Done on the parsed output rather than with a second pass over the markdown,
 * so the outline cannot disagree with what is actually on screen.
 *
 * What gets rendered is the *manuscript*, not the raw source: this is the
 * reading view, so slide blocks come out the same way they do in the exported
 * manuscript. The two annotating syntaxes keep their words: a `{slide}` heading
 * is still a heading, and a `:::slide` container is still whatever it wrapped —
 * only the markers are cut. That is the whole point of marking something rather
 * than moving it into a block, and is why a marked heading still appears in the
 * contents list and a projected scripture still appears in the reading.
 */
function useRenderedDocument(markdown: string): { html: string; landmarks: Landmark[] } {
  return useMemo(() => {
    const html = renderMarkdown(parseSlides(markdown || "").manuscript);
    if (!html) return { html: "", landmarks: [] };

    const parsed = new DOMParser().parseFromString(html, "text/html");
    const landmarks: Landmark[] = [];
    const used = new Set<string>();

    parsed.body.querySelectorAll("h1, h2, h3").forEach((heading) => {
      const text = (heading.textContent || "").trim();
      if (!text) return;

      const base =
        text
          .toLowerCase()
          .replace(/[^\w\s-]/g, "")
          .replace(/\s+/g, "-")
          .slice(0, 60) || "section";
      let id = base;
      let suffix = 2;
      while (used.has(id)) id = `${base}-${suffix++}`;
      used.add(id);

      heading.setAttribute("id", id);
      landmarks.push({ id, text, level: Number(heading.tagName[1]) });
    });

    return { html: parsed.body.innerHTML, landmarks };
  }, [markdown]);
}

export default function ViewDocumentPage() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { document: doc, isLoading, error } = useDocument(id);
  const { slideStyle } = useTheme();

  const [scale, setScale] = useState(() => {
    const stored = Number(localStorage.getItem(SCALE_KEY));
    return Number.isFinite(stored) && stored >= SCALE_MIN && stored <= SCALE_MAX
      ? stored
      : SCALE_DEFAULT;
  });
  const [chromeVisible, setChromeVisible] = useState(true);
  const [contentsOpen, setContentsOpen] = useState(false);
  const [progress, setProgress] = useState(0);

  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const scrollerRef = useRef<HTMLDivElement>(null);

  useWakeLock(true);

  const { html, landmarks } = useRenderedDocument(doc?.content || "");

  useDesktopDocument(
    doc
      ? {
          id: doc.id,
          title: doc.title,
          dirty: false,
          hasSlides: parseSlides(doc.content || "").slides.length > 0,
          save: () => undefined,
          print: () => {
            void runDocumentExport({
              kind: "manuscript-print",
              title: doc.title,
              content: doc.content || "",
              slideStyle,
            });
          },
          close: () => navigate("/"),
          getContent: () => doc.content || "",
          exportKind: (kind) => {
            void runDocumentExport({
              kind,
              title: doc.title,
              content: doc.content || "",
              slideStyle,
            });
          },
        }
      : null,
  );

  useEffect(() => {
    localStorage.setItem(SCALE_KEY, String(scale));
  }, [scale]);

  /**
   * Show the controls, and arm their retreat.
   *
   * The timer is never armed while the contents panel is open — a list you are
   * reading down should not vanish out from under you.
   */
  const revealChrome = useCallback(() => {
    setChromeVisible(true);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setChromeVisible(false), CHROME_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    revealChrome();
    return () => clearTimeout(hideTimerRef.current);
  }, [revealChrome]);

  useEffect(() => {
    if (contentsOpen) clearTimeout(hideTimerRef.current);
  }, [contentsOpen]);

  const handleSurfaceClick = useCallback(() => {
    if (contentsOpen) {
      setContentsOpen(false);
      revealChrome();
      return;
    }
    // A tap toggles: summon the controls, or dismiss them if they are already
    // up. Nothing else on this surface responds to a touch.
    if (chromeVisible) {
      clearTimeout(hideTimerRef.current);
      setChromeVisible(false);
    } else {
      revealChrome();
    }
  }, [chromeVisible, contentsOpen, revealChrome]);

  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const scrollable = el.scrollHeight - el.clientHeight;
    setProgress(scrollable > 0 ? Math.min(1, el.scrollTop / scrollable) : 0);
  }, []);

  const jumpTo = useCallback(
    (landmarkId: string) => {
      const target = window.document.getElementById(landmarkId);
      setContentsOpen(false);
      revealChrome();
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [revealChrome],
  );

  const adjustScale = useCallback(
    (direction: 1 | -1) => {
      setScale((current) =>
        Math.min(
          SCALE_MAX,
          Math.max(SCALE_MIN, Math.round((current + direction * SCALE_STEP) * 100) / 100),
        ),
      );
      revealChrome();
    },
    [revealChrome],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (contentsOpen) setContentsOpen(false);
        else revealChrome();
      }
      if (event.key === "+" || event.key === "=") adjustScale(1);
      if (event.key === "-") adjustScale(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [adjustScale, contentsOpen, revealChrome]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-8 h-8 border-t-2 border-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background">
        <FileText className="h-16 w-16 text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">Document not found</h1>
        <p className="text-muted-foreground mb-6">
          {error || "Unable to load the document."}
        </p>
        <Link href="/">
          <Button>Return to dashboard</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-background text-foreground">
      {/* How far through the sermon you are. Two pixels, always on — the one
          piece of chrome worth keeping visible while reading. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-30 h-0.5 bg-primary/70 origin-left transition-transform duration-150"
        style={{ transform: `scaleX(${progress})` }}
        aria-hidden="true"
      />

      <header
        className="reading-chrome desktop-chrome-offset absolute inset-x-0 top-0 z-20 flex items-center gap-1 border-b border-border/60 bg-background/90 px-3 py-2 backdrop-blur"
        data-visible={chromeVisible}
        // The controls are summoned by tapping the page; taps landing on the
        // bar itself must not immediately dismiss it again.
        onClick={(event) => event.stopPropagation()}
      >
        <Link href="/">
          <Button variant="ghost" size="icon" aria-label="Back to dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>

        <span className="min-w-0 flex-1 truncate px-1 text-sm font-medium">
          {doc.title}
        </span>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => adjustScale(-1)}
          disabled={scale <= SCALE_MIN}
          aria-label="Smaller text"
        >
          <Minus className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => adjustScale(1)}
          disabled={scale >= SCALE_MAX}
          aria-label="Larger text"
        >
          <Plus className="h-5 w-5" />
        </Button>

        {landmarks.length > 0 && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setContentsOpen((open) => !open)}
            aria-label={contentsOpen ? "Close contents" : "Contents"}
            aria-expanded={contentsOpen}
          >
            {contentsOpen ? <X className="h-5 w-5" /> : <List className="h-5 w-5" />}
          </Button>
        )}

        <Link href={`/documents/${doc.id}/edit`}>
          <Button variant="ghost" size="icon" aria-label="Edit document">
            <Pencil className="h-5 w-5" />
          </Button>
        </Link>
      </header>

      {contentsOpen && (
        <nav
          className="absolute inset-x-0 top-[52px] z-10 max-h-[60vh] overflow-y-auto border-b border-border bg-background/98 px-3 py-2 shadow-lg backdrop-blur"
          onClick={(event) => event.stopPropagation()}
          aria-label="Contents"
        >
          {landmarks.map((landmark) => (
            <button
              key={landmark.id}
              type="button"
              onClick={() => jumpTo(landmark.id)}
              className={cn(
                "block w-full truncate rounded-md px-3 py-2.5 text-left text-sm hover:bg-muted",
                landmark.level === 2 && "pl-6 text-muted-foreground",
                landmark.level === 3 && "pl-9 text-muted-foreground",
              )}
            >
              {landmark.text}
            </button>
          ))}
        </nav>
      )}

      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        onClick={handleSurfaceClick}
        className="flex-1 overflow-y-auto"
      >
        <div
          className="reading-surface pb-[40vh] pt-[18vh]"
          style={{ ["--reading-scale" as string]: scale }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
}
